import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Check, ChevronRight, Package, MapPin, DollarSign, ReceiptText, ShieldCheck, ShieldAlert, ShieldX, Loader2, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import AddressAutocomplete from "../components/AddressAutocomplete";

const steps = [
  { key: "addresses", label: "Addresses", icon: MapPin },
  { key: "package", label: "Package", icon: Package },
  { key: "insurance", label: "Insurance", icon: ShieldCheck },
  { key: "rates", label: "Rate", icon: DollarSign },
  { key: "review", label: "Review", icon: ReceiptText },
];

const emptyAddr = { label: "", name: "", company: "", address1: "", address2: "", city: "", state: "", zip: "", country: "US", phone: "", email: "" };

export default function NewShipment() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [book, setBook] = useState({
    sender: { ...emptyAddr },
    receiver: { ...emptyAddr },
    pieces: [{ weight: 2, length: 10, width: 8, height: 6, declared_value: 0, description: "" }],
    reference: "",
    signature_required: false,
    chargeback_guarantee_value: 0,
    insurance_disclosure_accepted: false,
    insurance_override_code: "",
  });
  const [addresses, setAddresses] = useState([]);
  const [rates, setRates] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [loading, setLoading] = useState(false);

  // Populate sender on mount:
  // 1) Prefer the address marked as default sender (or first address) from address book.
  // 2) Fall back to the user's profile info (name/email/phone/company).
  useEffect(() => {
    api.get("/addresses").then(({ data }) => {
      setAddresses(data);
      if (!data || !data.length) return;
      // Find a sender-flagged or "Sender" / "Warehouse" / "Default" labeled entry
      const lower = (s) => (s || "").toString().toLowerCase();
      const senderAddr =
        data.find((a) => a.is_default_sender) ||
        data.find((a) => /sender|warehouse|default|hq|home/.test(lower(a.label))) ||
        data[0];
      if (senderAddr) {
        const { address_id, user_id, tenant_id, created_at, is_default_sender, ...rest } = senderAddr;
        setBook((b) => ({
          ...b,
          sender: {
            ...rest,
            // Always overlay user contact info if the address has none
            name: rest.name || user?.name || "",
            email: rest.email || user?.email || "",
            phone: rest.phone || user?.phone || "",
            company: rest.company || user?.company || "",
          },
        }));
      }
    }).catch(() => {});
  }, [user]);

  // If no address book entries exist, still seed contact fields from the user profile
  useEffect(() => {
    if (!user) return;
    setBook((b) => {
      if (b.sender.name || b.sender.email) return b; // already populated
      return {
        ...b,
        sender: {
          ...b.sender,
          name: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
          company: user.company || "",
        },
      };
    });
  }, [user]);

  const pickAddr = (kind, a) => {
    const { address_id, user_id, tenant_id, created_at, ...rest } = a;
    setBook((b) => ({ ...b, [kind]: rest }));
  };

  const setField = (kind, key, value) =>
    setBook((b) => ({ ...b, [kind]: { ...b[kind], [key]: value } }));

  const setPiece = (i, key, value) =>
    setBook((b) => {
      const pcs = [...b.pieces];
      pcs[i] = { ...pcs[i], [key]: key === "description" ? value : parseFloat(value) || 0 };
      return { ...b, pieces: pcs };
    });

  const quote = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/shipments/quote", {
        sender: book.sender, receiver: book.receiver, pieces: book.pieces,
      });
      setRates(data.rates);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Quote failed");
    } finally { setLoading(false); }
  };

  const bookShipment = async () => {
    if (!chosen) return;
    setLoading(true);
    try {
      // When the rate came from EasyPost, encode the EP shipment+rate IDs into the
      // booking reference so the backend can call easypost_client.buy() directly.
      const epRef = chosen.easypost_rate_id && chosen.easypost_shipment_id
        ? `ep:${chosen.easypost_shipment_id}:${chosen.easypost_rate_id}`
        : (book.reference || "");
      const { data } = await api.post("/shipments", {
        sender: book.sender, receiver: book.receiver, pieces: book.pieces,
        carrier_code: chosen.carrier_code, service_code: chosen.service_code,
        package_type_code: chosen.package_type_code || "PKG",
        reference: epRef,
        signature_required: !!book.signature_required,
        chargeback_guarantee_value: parseFloat(book.chargeback_guarantee_value) || 0,
        insurance_disclosure_accepted: !!book.insurance_disclosure_accepted,
        insurance_override_code: book.insurance_override_code || undefined,
        commodity_category: book.commodity_category || "general_merchandise",
        contents: book.contents || "",
        items_sent: book.items_sent || [],
        bill_to: book.bill_to || "my_account",
        third_party_account: book.third_party_account || "",
      });
      toast.success(`Booked! ${data.book_number}`);
      await refresh();
      navigate(`/dashboard/track/${data.book_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally { setLoading(false); }
  };

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">New shipment</div>
        <h1 className="font-display text-3xl font-black tracking-tighter">Create a shipment</h1>
      </div>

      <div className="flex items-center mb-6 sm:mb-8 overflow-x-auto pb-2">
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className={`flex items-center gap-2 shrink-0 ${i <= step ? "text-zinc-900" : "text-zinc-400"}`}>
              <div className={`w-7 h-7 rounded-sm flex items-center justify-center text-xs font-mono-plex ${i < step ? "brand-bg text-white" : i === step ? "bg-zinc-900 text-white" : "border border-zinc-300"}`}>
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap">{s.label}</span>
            </div>
            {i < steps.length - 1 && <ChevronRight size={16} className="mx-2 sm:mx-3 text-zinc-300 shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          <AddressForm title="Sender" data={book.sender} onChange={(k, v) => setField("sender", k, v)}
            addresses={addresses} onPick={(a) => pickAddr("sender", a)} testPrefix="sender" />
          <AddressForm title="Receiver" data={book.receiver} onChange={(k, v) => setField("receiver", k, v)}
            addresses={addresses} onPick={(a) => pickAddr("receiver", a)} testPrefix="receiver" />
          <div className="md:col-span-2">
            <PreFraudCheck receiver={book.receiver} />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button data-testid="to-package-btn" onClick={() => setStep(1)}
              disabled={!book.sender.zip || !book.receiver.zip}
              className="brand-bg text-white px-6 py-2 rounded-sm disabled:opacity-50">
              Next: Package →
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <div className="font-display text-xl font-bold mb-4">Package details</div>
          {book.pieces.map((p, i) => (
            <div key={i} className="grid md:grid-cols-5 gap-3 mb-3">
              <Field label="Weight (lb)" value={p.weight} onChange={(v) => setPiece(i, "weight", v)} testId={`piece-${i}-weight`} />
              <Field label="Length (in)" value={p.length} onChange={(v) => setPiece(i, "length", v)} testId={`piece-${i}-length`} />
              <Field label="Width (in)" value={p.width} onChange={(v) => setPiece(i, "width", v)} testId={`piece-${i}-width`} />
              <Field label="Height (in)" value={p.height} onChange={(v) => setPiece(i, "height", v)} testId={`piece-${i}-height`} />
              <Field label="Declared value ($)" value={p.declared_value} onChange={(v) => setPiece(i, "declared_value", v)} testId={`piece-${i}-value`} />
            </div>
          ))}
          <div className="mt-4">
            <Field label="Reference (PO, invoice #)" value={book.reference} onChange={(v) => setBook({ ...book, reference: v })} testId="shipment-reference" text />
          </div>
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(0)} className="border border-zinc-300 px-5 py-2 rounded-sm text-sm">← Back</button>
            <button data-testid="to-insurance-btn" onClick={() => setStep(2)}
              className="brand-bg text-white px-6 py-2 rounded-sm">
              Next: Insurance →
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <InsuranceStep
          book={book}
          setBook={setBook}
          onBack={() => setStep(1)}
          onNext={quote}
          loading={loading}
        />
      )}

      {step === 3 && (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="font-display text-xl font-bold">Select a carrier & service</div>
            <div data-testid="rates-source-badge" className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-mono-plex uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Live rates · Multi-carrier · Multi-aggregator
            </div>
          </div>
          <div className="space-y-2">
            {(() => {
              // Build comparison context for badge generation
              const cheapest = rates.reduce((m, r) => (r.total ?? r.subtotal ?? Infinity) < (m.total ?? m.subtotal ?? Infinity) ? r : m, rates[0] || {});
              const withTransit = rates.filter((r) => r.transit_days && r.transit_days > 0);
              const fastest = withTransit.length ? withTransit.reduce((m, r) => r.transit_days < m.transit_days ? r : m, withTransit[0]) : null;
              // Group by normalized service to find same-lane price diffs across providers
              const byService = {};
              rates.forEach((r) => {
                const key = (r.service_code || "").toUpperCase();
                if (!key) return;
                (byService[key] = byService[key] || []).push(r);
              });
              const lanePriceDelta = (r) => {
                const peers = byService[(r.service_code || "").toUpperCase()] || [];
                if (peers.length < 2) return null;
                const others = peers.filter((p) => p !== r);
                const otherCheapest = others.reduce((m, p) => (p.total ?? p.subtotal) < (m.total ?? m.subtotal) ? p : m, others[0]);
                const mine = r.total ?? r.subtotal ?? 0;
                const theirs = otherCheapest.total ?? otherCheapest.subtotal ?? 0;
                if (mine < theirs) return { kind: "winner", delta: theirs - mine, vs: otherCheapest.provider_label };
                if (mine > theirs) return { kind: "loser", delta: mine - theirs, vs: otherCheapest.provider_label };
                return null;
              };

              return rates.map((r, i) => {
                const carrierLabel = (r.carrier_name || r.carrier_code || "")
                  .replace(/DEFAULT$/i, "")
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase());
                const serviceLabel = (r.service_name || r.service_code || "")
                  .replace(/_/g, " ")
                  .replace(/^[A-Z][a-z]+ /, "");

                const isCheapest = r === cheapest;
                const isFastest = r === fastest;
                const lane = lanePriceDelta(r);

                return (
                  <div
                    key={i}
                    data-testid={`rate-option-${r.carrier_code}-${r.service_code}`}
                    onClick={() => setChosen(r)}
                    className={`bg-white border rounded-sm p-4 cursor-pointer transition-all ${
                      chosen === r
                        ? "border-[var(--brand-accent)] shadow-md"
                        : isCheapest
                        ? "border-emerald-300 hover:border-emerald-500"
                        : "border-zinc-200 hover:border-zinc-400"
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {r.provider_label && (
                            <span data-testid={`provider-badge-${i}`} className="text-[10px] font-mono-plex uppercase tracking-wider bg-zinc-900 text-white px-2 py-0.5 rounded-sm shrink-0">
                              {r.provider_label}
                            </span>
                          )}
                          <div className="font-display font-bold text-base sm:text-lg truncate">{carrierLabel} — {serviceLabel}</div>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          {isCheapest && (
                            <span data-testid={`badge-cheapest-${i}`} className="text-[10px] font-mono-plex uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded-sm">
                              Cheapest overall
                            </span>
                          )}
                          {isFastest && !isCheapest && (
                            <span data-testid={`badge-fastest-${i}`} className="text-[10px] font-mono-plex uppercase tracking-wider bg-blue-600 text-white px-1.5 py-0.5 rounded-sm">
                              Fastest overall
                            </span>
                          )}
                          {lane?.kind === "winner" && (
                            <span data-testid={`badge-lane-winner-${i}`} className="text-[10px] font-mono-plex uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-300 px-1.5 py-0.5 rounded-sm">
                              ${lane.delta.toFixed(2)} cheaper than {lane.vs}
                            </span>
                          )}
                          {lane?.kind === "loser" && (
                            <span data-testid={`badge-lane-loser-${i}`} className="text-[10px] font-mono-plex uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded-sm">
                              ${lane.delta.toFixed(2)} pricier than {lane.vs}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 mt-1.5">
                          {r.transit_days ? `Transit: ${r.transit_days} days` : ""}
                        </div>
                      </div>
                      <div className="text-left sm:text-right shrink-0">
                        <div data-testid={`rate-total-${i}`} className="font-display font-black text-xl sm:text-2xl">${(r.total || 0).toFixed(2)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-0.5">All-in price</div>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(2)} className="border border-zinc-300 px-5 py-2 rounded-sm text-sm">← Back</button>
            <button data-testid="to-review-btn" onClick={() => setStep(4)} disabled={!chosen}
              className="brand-bg text-white px-6 py-2 rounded-sm disabled:opacity-50">
              Review & book →
            </button>
          </div>
        </div>
      )}

      {step === 4 && chosen && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6 max-w-2xl">
          <div className="font-display text-xl font-bold mb-4">Confirm shipment</div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Summary title="From" data={book.sender} />
            <Summary title="To" data={book.receiver} />
          </div>
          <div className="mt-4 border-t border-zinc-200 pt-4 text-sm">
            <div><b>Carrier:</b> {chosen.carrier_name} · {chosen.service_name}</div>
            <div><b>Pieces:</b> {book.pieces.length} · Weight: {book.pieces.reduce((a, p) => a + p.weight, 0)} lb</div>
            {book.signature_required && <div><b>Signature:</b> Required</div>}
            {book.chargeback_guarantee_value > 0 && <div><b>Chargeback guarantee:</b> ${Number(book.chargeback_guarantee_value).toFixed(2)}</div>}
            {book.reference && <div><b>Reference:</b> {book.reference}</div>}
          </div>
          <div className="mt-6 bg-zinc-50 border border-zinc-200 p-4 rounded-sm">
            <div className="flex justify-between text-sm"><span className="text-zinc-500">All-in shipping price</span><span className="text-zinc-500 text-xs">includes carrier, fuel, signature & handling</span></div>
            <div className="flex justify-between mt-2 pt-2 border-t border-zinc-300 font-display font-black text-lg"><span>Total</span><span data-testid="review-total">${chosen.total.toFixed(2)}</span></div>
          </div>
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(3)} className="border border-zinc-300 px-5 py-2 rounded-sm text-sm">← Back</button>
            <button data-testid="confirm-book-btn" onClick={bookShipment} disabled={loading}
              className="brand-bg text-white px-6 py-2 rounded-sm disabled:opacity-50">
              {loading ? "Booking…" : `Book & charge wallet →`}
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, value, onChange, testId, text = false }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-zinc-500">{label}</label>
      <input data-testid={testId} type={text ? "text" : "number"} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-[var(--brand-accent)]" />
    </div>
  );
}

function AddressForm({ title, data, onChange, addresses, onPick, testPrefix }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-lg font-bold">{title}</div>
        {addresses.length > 0 && (
          <select data-testid={`${testPrefix}-address-picker`} onChange={(e) => {
            const a = addresses.find((x) => x.address_id === e.target.value); if (a) onPick(a);
          }} className="text-sm border border-zinc-300 rounded-sm px-2 py-1">
            <option value="">Use saved…</option>
            {addresses.map((a) => <option key={a.address_id} value={a.address_id}>{a.label} — {a.name}</option>)}
          </select>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" text value={data.name} onChange={(v) => onChange("name", v)} testId={`${testPrefix}-name`} />
        <Field label="Company" text value={data.company} onChange={(v) => onChange("company", v)} testId={`${testPrefix}-company`} />
        <div className="col-span-2">
          <label className="text-[11px] uppercase tracking-wider text-zinc-500">Address</label>
          <div className="mt-1">
            <AddressAutocomplete
              testId={`${testPrefix}-address1`}
              value={data.address1}
              onChange={(v) => onChange("address1", v)}
              onSelect={(a) => {
                onChange("address1", a.address1 || a.formatted || "");
                if (a.address2) onChange("address2", a.address2);
                if (a.city) onChange("city", a.city);
                if (a.state) onChange("state", a.state);
                if (a.zip) onChange("zip", a.zip);
                if (a.country) onChange("country", a.country);
              }}
              placeholder="Start typing address — Google Places autocomplete"
            />
          </div>
        </div>
        <Field label="City" text value={data.city} onChange={(v) => onChange("city", v)} testId={`${testPrefix}-city`} />
        <Field label="State" text value={data.state} onChange={(v) => onChange("state", v)} testId={`${testPrefix}-state`} />
        <Field label="Zip" text value={data.zip} onChange={(v) => onChange("zip", v)} testId={`${testPrefix}-zip`} />
        <Field label="Country" text value={data.country} onChange={(v) => onChange("country", v)} testId={`${testPrefix}-country`} />
        <div className="col-span-2"><Field label="Phone" text value={data.phone} onChange={(v) => onChange("phone", v)} testId={`${testPrefix}-phone`} /></div>
      </div>
    </div>
  );
}

function Summary({ title, data }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-zinc-500">{title}</div>
      <div className="mt-1 text-sm">
        <div className="font-medium">{data.name}</div>
        <div className="text-zinc-600">{data.address1}</div>
        <div className="text-zinc-600">{data.city}, {data.state} {data.zip}</div>
        <div className="text-zinc-600">{data.country}</div>
      </div>
    </div>
  );
}

function PreFraudCheck({ receiver }) {
  const [state, setState] = useState({ status: "idle", data: null });
  // Debounced auto-run once the receiver has enough info (email OR zip + city + name)
  useEffect(() => {
    const hasMin = (receiver.email && receiver.zip) || (receiver.zip && receiver.city && receiver.name);
    if (!hasMin) {
      setState({ status: "idle", data: null });
      return;
    }
    setState((s) => ({ ...s, status: "loading" }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/shipments/pre-check", { receiver });
        setState({ status: "ok", data });
      } catch {
        setState({ status: "err", data: null });
      }
    }, 700);
    return () => clearTimeout(t);
  }, [receiver.email, receiver.zip, receiver.city, receiver.name]);

  if (state.status === "idle") return null;
  if (state.status === "loading") {
    return (
      <div data-testid="prefraud-loading" className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-xs text-zinc-500 inline-flex items-center gap-2">
        <Loader2 className="animate-spin" size={12} /> Running pre-shipment risk check…
      </div>
    );
  }
  if (state.status === "err" || !state.data) return null;
  const { badge, reasons, suggested_service } = state.data;
  const cfg = {
    low:    { Icon: ShieldCheck, color: "bg-emerald-50 border-emerald-300 text-emerald-900",  iconColor: "text-emerald-600",  title: "Low risk — clear to ship",        sub: "All vendor signals look good for this buyer." },
    review: { Icon: ShieldAlert, color: "bg-amber-50 border-amber-300 text-amber-900",        iconColor: "text-amber-600",    title: "Manual review recommended",        sub: "Some signals warrant a second look before booking." },
    block:  { Icon: ShieldX,     color: "bg-red-50 border-red-300 text-red-900",              iconColor: "text-red-600",      title: "High risk — booking discouraged",  sub: "Multiple high-risk signals were detected." },
  }[badge] || { Icon: ShieldCheck, color: "bg-zinc-50 border-zinc-300 text-zinc-700", iconColor: "text-zinc-500", title: "Risk unknown", sub: "" };
  const Icon = cfg.Icon;
  return (
    <div data-testid={`prefraud-badge-${badge}`} className={`border rounded-sm p-3 ${cfg.color} flex items-start gap-3`}>
      <Icon size={20} className={`mt-0.5 shrink-0 ${cfg.iconColor}`} />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold">{cfg.title}</div>
        <div className="text-xs opacity-80 mt-0.5">{cfg.sub}</div>
        {reasons && reasons.length > 0 && (
          <ul className="mt-2 text-xs space-y-0.5">
            {reasons.map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        )}
        {suggested_service && (
          <div className="mt-2 text-xs">
            <a data-testid="prefraud-suggest-link" href="/dashboard/smart-services" className="underline font-medium">
              Suggested: activate Smart Start protection →
            </a>
          </div>
        )}
      </div>
    </div>
  );
}


function InsuranceStep({ book, setBook, onBack, onNext, loading }) {
  const declared = parseFloat(book.pieces.reduce((sum, p) => sum + (parseFloat(p.declared_value) || 0), 0).toFixed(2));
  const chargeback = parseFloat(book.chargeback_guarantee_value) || 0;
  const mismatch = chargeback > 0 && Math.abs(declared - chargeback) > 0.01;
  const requiresHighValueCode = Math.max(declared, chargeback) > 15000;
  const requiresAck = mismatch && !book.insurance_disclosure_accepted;
  const isInternational = (book.receiver?.country || "US").toUpperCase() !== "US";

  const DOMESTIC_COMMODITIES = [
    { v: "general_merchandise", l: "General merchandise" },
    { v: "electronics",         l: "Electronics" },
    { v: "antiques_art",        l: "Antiques, art & collectibles" },
    { v: "fragile",             l: "Fragile (NOT insured for breakage)", warn: true },
    { v: "jewelry",             l: "Jewelry" },
    { v: "stones",              l: "Loose stones" },
    { v: "watches",             l: "Watches" },
    { v: "perishable",          l: "Perishable (NOT insured)",          warn: true },
  ];

  const items = book.items_sent || [];
  const addItem = () => setBook({ ...book, items_sent: [...items, { description: "", quantity: 1, value: 0, commodity: book.commodity_category || "general_merchandise", country_of_origin: "" }] });
  const updateItem = (i, k, v) => {
    const next = items.map((it, idx) => idx === i ? { ...it, [k]: v } : it);
    setBook({ ...book, items_sent: next });
  };
  const removeItem = (i) => setBook({ ...book, items_sent: items.filter((_, idx) => idx !== i) });

  const commodityWarn = DOMESTIC_COMMODITIES.find((c) => c.v === book.commodity_category)?.warn;
  const minItems = isInternational ? 1 : 0;
  const hasMissingItems = isInternational && items.length === 0;
  const canProceed = !requiresAck && !hasMissingItems && (!requiresHighValueCode || (book.insurance_override_code && book.insurance_override_code.length >= 4));

  return (
    <div data-testid="insurance-step" className="bg-white border border-zinc-200 rounded-sm p-6 max-w-3xl">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} className="text-[var(--brand-accent)]" />
        <div className="font-display text-xl font-bold">Insurance & coverage</div>
      </div>
      <p className="text-sm text-zinc-600 mb-5">
        Declared value protects the carrier's liability. Chargeback guarantee
        value covers payment-dispute reimbursement (SelleCheck). Both should
        match — if they don't, you'll need to acknowledge the disclosure.
      </p>

      {/* Top row: declared + chargeback */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div data-testid="declared-value-card" className="bg-zinc-50 border border-zinc-200 rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Declared value (sum of pieces)</div>
          <div className="font-display text-2xl font-black tracking-tighter">${declared.toFixed(2)}</div>
          <div className="text-[11px] text-zinc-500 mt-1">Set on the Package step. Carrier's liability ceiling for loss.</div>
        </div>
        <div>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Chargeback guarantee / value of sale ($)</div>
            <input data-testid="chargeback-value-input" type="number" step="0.01" min="0" value={book.chargeback_guarantee_value}
              onChange={(e) => setBook({ ...book, chargeback_guarantee_value: e.target.value, insurance_disclosure_accepted: false })}
              placeholder={`Recommended: ${declared.toFixed(2)}`}
              className="w-full border border-zinc-300 rounded-sm px-3 py-2 font-mono-plex" />
          </label>
          <p className="text-[11px] text-zinc-500 mt-1">SelleCheck protection limit if the buyer files a chargeback.</p>
        </div>
      </div>

      {/* Commodity category */}
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Commodity category</div>
          <select data-testid="commodity-category-select" value={book.commodity_category || "general_merchandise"}
            onChange={(e) => setBook({ ...book, commodity_category: e.target.value })}
            className="w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white text-sm">
            {DOMESTIC_COMMODITIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
          {commodityWarn && (
            <div data-testid="commodity-warning" className="text-[11px] text-amber-700 mt-1">
              ⚠ This commodity has limited coverage — review carrier terms before booking.
            </div>
          )}
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Contents description</div>
          <input data-testid="contents-input" type="text" value={book.contents || ""}
            onChange={(e) => setBook({ ...book, contents: e.target.value })}
            placeholder="e.g., One men's wristwatch"
            className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm" />
        </label>
      </div>

      {/* Items sent — required for international, optional for domestic */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Items sent{isInternational && <span className="text-red-600 ml-1">*</span>}</div>
            <div className="text-[11px] text-zinc-500">
              {isInternational ? "Required for international — each item needs its own commodity code & country of origin." : "Optional itemization (appears on the insurance form & customs docs)."}
            </div>
          </div>
          <button data-testid="add-item-btn" type="button" onClick={addItem}
            className="text-xs border border-zinc-300 rounded-sm px-2 py-1 inline-flex items-center gap-1 hover:bg-zinc-50">
            <Plus size={12} /> Add item
          </button>
        </div>
        {items.length === 0 && (
          <div className={`border border-dashed rounded-sm p-3 text-xs text-zinc-500 text-center ${hasMissingItems ? "border-red-300 bg-red-50 text-red-700" : "border-zinc-300"}`}>
            {hasMissingItems ? "Add at least one item — international shipments require itemization." : "No items added."}
          </div>
        )}
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 mb-2 items-end" data-testid={`item-row-${i}`}>
            <input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} placeholder="Description"
              className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm" data-testid={`item-${i}-description`} />
            <input type="number" min="1" value={it.quantity} onChange={(e) => updateItem(i, "quantity", parseInt(e.target.value) || 1)} placeholder="Qty"
              className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm font-mono-plex" data-testid={`item-${i}-quantity`} />
            <input type="number" step="0.01" min="0" value={it.value} onChange={(e) => updateItem(i, "value", parseFloat(e.target.value) || 0)} placeholder="Value $"
              className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm font-mono-plex" data-testid={`item-${i}-value`} />
            {isInternational ? (
              <input value={it.country_of_origin} onChange={(e) => updateItem(i, "country_of_origin", e.target.value.toUpperCase().slice(0, 2))} placeholder="C/O (US)"
                maxLength={2} className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm font-mono-plex uppercase" data-testid={`item-${i}-country`} />
            ) : (
              <select value={it.commodity || "general_merchandise"} onChange={(e) => updateItem(i, "commodity", e.target.value)}
                className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid={`item-${i}-commodity`}>
                {DOMESTIC_COMMODITIES.map((c) => <option key={c.v} value={c.v}>{c.l.split(" (")[0]}</option>)}
              </select>
            )}
            <button type="button" onClick={() => removeItem(i)} className="text-zinc-400 hover:text-red-600 px-2" data-testid={`item-${i}-remove`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      {/* Signature requirement */}
      <label className="mt-4 flex items-start gap-2 cursor-pointer">
        <input data-testid="signature-required-checkbox" type="checkbox" checked={!!book.signature_required}
          onChange={(e) => setBook({ ...book, signature_required: e.target.checked })}
          className="mt-1" />
        <div className="text-sm">
          <div className="font-medium">Require signature on delivery</div>
          <div className="text-[11px] text-zinc-500">Adds a small signature handling fee. Required by some insurers for items over $1,000.</div>
        </div>
      </label>

      {/* Bill-to selector */}
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Bill this shipment to</div>
        <div className="grid sm:grid-cols-3 gap-2">
          {[
            { v: "my_account",   l: "My account",   d: "Debit your wallet." },
            { v: "third_party",  l: "Third party",  d: "Bill another party's account." },
            { v: "customer",     l: "Customer",     d: "Customer pays on receipt." },
          ].map((o) => {
            const active = (book.bill_to || "my_account") === o.v;
            return (
              <button key={o.v} type="button" data-testid={`bill-to-${o.v}`}
                onClick={() => setBook({ ...book, bill_to: o.v })}
                className={`text-left border rounded-sm p-3 ${active ? "border-[var(--brand-accent)] bg-blue-50" : "border-zinc-300 hover:bg-zinc-50"}`}>
                <div className="font-display font-bold text-sm flex items-center gap-1">
                  {o.l}
                  {active && <Check size={14} className="text-emerald-600" />}
                </div>
                <div className="text-[11px] text-zinc-600 mt-1">{o.d}</div>
              </button>
            );
          })}
        </div>
        {book.bill_to === "third_party" && (
          <input data-testid="third-party-account" value={book.third_party_account || ""}
            onChange={(e) => setBook({ ...book, third_party_account: e.target.value })}
            placeholder="Third-party account number"
            className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono-plex" />
        )}
      </div>

      {/* Mismatch disclosure */}
      {mismatch && (
        <div data-testid="insurance-mismatch-disclosure" className="mt-4 bg-amber-50 border border-amber-300 rounded-sm p-4 text-sm text-amber-900">
          <div className="font-display font-bold flex items-center gap-2">
            <ShieldAlert size={16} className="text-amber-600" /> Values do not match
          </div>
          <p className="mt-1 text-[13px]">
            Declared value (<b>${declared.toFixed(2)}</b>) and chargeback guarantee
            (<b>${chargeback.toFixed(2)}</b>) are not equal. SellePack's insurance
            partner may decline coverage for the unprotected portion. You must
            acknowledge this disclosure to proceed.
          </p>
          <label className="mt-2 flex items-start gap-2 cursor-pointer">
            <input data-testid="insurance-disclosure-ack" type="checkbox" checked={!!book.insurance_disclosure_accepted}
              onChange={(e) => setBook({ ...book, insurance_disclosure_accepted: e.target.checked })}
              className="mt-1" />
            <span className="text-[13px]">
              I understand that loss / chargeback coverage will be limited to the
              lower of the two values, and any difference is not insured.
            </span>
          </label>
        </div>
      )}

      {/* High-value (> $15k) authorization code */}
      {requiresHighValueCode && (
        <div data-testid="insurance-highvalue-code" className="mt-4 bg-red-50 border border-red-300 rounded-sm p-4 text-sm text-red-900">
          <div className="font-display font-bold flex items-center gap-2">
            <ShieldAlert size={16} className="text-red-600" /> High-value authorization required
          </div>
          <p className="mt-1 text-[13px]">
            Any insurance or chargeback value over <b>$15,000</b> must be authorized
            by phone. Call your account manager to obtain a one-time authorization
            code, then enter it below.
          </p>
          <input data-testid="insurance-override-code" type="text" value={book.insurance_override_code}
            onChange={(e) => setBook({ ...book, insurance_override_code: e.target.value.toUpperCase() })}
            placeholder="Authorization code"
            className="mt-3 w-full border border-red-300 bg-white rounded-sm px-3 py-2 font-mono-plex uppercase tracking-widest" />
        </div>
      )}

      {/* International notice */}
      {isInternational && (
        <div data-testid="insurance-international-notice" className="mt-4 bg-blue-50 border border-blue-300 rounded-sm p-4 text-sm text-blue-900">
          <div className="font-display font-bold flex items-center gap-2">
            <ShieldCheck size={16} className="text-blue-600" /> International shipment
          </div>
          <p className="mt-1 text-[13px]">
            International shipments require itemization with country of origin per
            item — these populate the customs declaration. A 10% international
            handling surcharge is included in your displayed rate. Terms vary by
            destination country.
          </p>
        </div>
      )}

      <div className="flex justify-between mt-6">
        <button data-testid="insurance-back-btn" onClick={onBack}
          className="border border-zinc-300 px-5 py-2 rounded-sm text-sm">← Back</button>
        <button data-testid="insurance-next-btn" onClick={onNext} disabled={!canProceed || loading}
          className="brand-bg text-white px-6 py-2 rounded-sm disabled:opacity-50">
          {loading ? "Fetching rates…" : "Continue → Rates"}
        </button>
      </div>
    </div>
  );
}

